import { shellStringResources } from '../../Resources/ShellStringResources';
import { sanitizeAndFormatString, stringsAreEqualCaseInsensitive } from '../../../lib/shared/utils/string.util';
import { IModel, Collection, IViolation } from '../base/annotation';
import { Metamodel } from '../base/model';
import { Named } from '../base/named';
import { OwnedSet } from '../base/ownedset';
import { Parameter } from '../base/parameter';
import { IFactoryIdentity } from '../../interfaces/IFactoryIdentity';
import { IFactoryEncryption } from '../../interfaces/IFactoryEncryption';
import { LinkedService } from '../linkedservice/linkedservice';

export enum FactoryTrustMIMode {
    Enabled = 'Enabled',
    Disabled = 'Disabled'
}

/**
 * Represents a factory which can group a set of concept entities such as pipelines, datasets and data flows.
 */
@Metamodel.model({
    name: 'factory',
    referable: true,
    reusable: true,
    display: [shellStringResources.DataFactory, shellStringResources.DataFactories]
})
export class Factory extends Named {
    public static readonly MODEL: IModel;
    public static readonly scopeActivityRestrictionKey: string = 'ADFScopeSupport';

    @Metamodel.attribute({
        type: Parameter.MODEL,
        collection: Collection.Set,
        constraint: {
            unique: true,
            validator: Factory.validateParameters
        }
    })
    public readonly parameters = OwnedSet.create<Parameter>(this);

    @Metamodel.attribute({
        collection: Collection.Map
    })
    public readonly configurations: { [key: string]: string } = {};

    @Metamodel.attribute({
        display: shellStringResources.ModelName_LinkedService,
        reference: true,
        type: LinkedService.MODEL
    })
    public stagingLinkedService?: LinkedService;
    public location: string;
    public identity: IFactoryIdentity;

    public encryption: IFactoryEncryption;

    @Metamodel.attribute({
        collection: Collection.Map
    })
    public trustModeClaimForMi?: { value: FactoryTrustMIMode, editable: boolean };

    public constructor(name: string, location: string) {
        super(name);
        this.location = location;
    }

    public static hasScopeSupportEnabled(factory: Factory): boolean {
        if (factory && factory.parameters && factory.parameters.size) {
            for (const parameter of factory.parameters) {
                if (stringsAreEqualCaseInsensitive(parameter.name, Factory.scopeActivityRestrictionKey)
                    && stringsAreEqualCaseInsensitive(parameter._type, 'bool') && parameter.defaultValue === 'true') {
                    return true;
                }
            }
        }
        return false;
    }

    private static validateParameters(factory: Factory): IViolation[] | undefined {
        const violations: IViolation[] = [];
        if (factory.parameters && factory.parameters.size) {
            for (const parameter of factory.parameters) {
                if (parameter.name && !parameter.defaultValue && parameter._type !== 'securestring') {
                    violations.push({
                        context: factory,
                        message: () => sanitizeAndFormatString(shellStringResources.FactoryGlobalParameterValueError, parameter.name)
                    });
                }
            }
        }
        return violations;
    }
}
